/**
 * The side rail both applications hang their navigation in.
 *
 * Brand block, a scrolling list, a pinned footer. The assistant fills the list with a conversation
 * history grouped by recency; the back office fills it with destinations.
 *
 * ## Why a rail rather than the top bar this replaced
 *
 * A top bar was fine for three destinations. It is wrong for the assistant, whose left column is a
 * *conversation history* grouped by recency — a list of unpredictable length, and a list does not
 * fit in a horizontal bar. The rail also gives both applications room for a footer, which is where
 * sign-out belongs: reachable from every screen without sitting next to the destructive actions in
 * the page itself.
 *
 * ## One tone, where there used to be two — and what that costs
 *
 * The previous rail came in light for the assistant and navy for the back office, and the navy was
 * doing real work: it meant an administrator with both applications open could tell at a glance
 * which one they were about to act in.
 *
 * The new charte puts the same light rail in both. That is what the mockups show for the chat, the
 * knowledge base and conversation supervision alike, so it is implemented as drawn — but the
 * distinguisher is genuinely gone, and it is worth knowing that it was removed rather than
 * overlooked. What separates the two applications now is the page heading and the contents of the
 * rail itself, both of which need reading. If the bank ever reports an administrator acting in the
 * wrong application, this is the change that made it possible.
 *
 * ## Direction
 *
 * "Side" is inline-start, so the rail is on the left in English and on the right in Arabic. That is
 * the whole reason it is a flex sibling of the main region rather than a positioned element — the
 * browser mirrors flex order for free, and every position value here would have needed a logical
 * property and a second look.
 */

import type { ReactNode } from "react";

import { RouteLink } from "../router/router";
import { cx } from "./cx";

export interface SideNavProps {
  /** Accessible name for the nav landmark, localized by the caller. */
  label: string;
  /** The brand block at the top. */
  brand?: ReactNode;
  /** Items, groups, or anything else. */
  children: ReactNode;
  /** Pinned to the bottom: the disclaimer card, the language toggle, sign-out. */
  footer?: ReactNode;
  /**
   * Sits directly under the brand, above the scrolling list, and does not scroll with it.
   *
   * For the assistant's "New conversation" button, which the mockup keeps visible however long the
   * history gets — an action you reach for *because* the list is long is the wrong one to put at
   * the top of that list.
   */
  action?: ReactNode;
  className?: string;
}

export function SideNav({
  label,
  brand,
  children,
  footer,
  action,
  className,
}: SideNavProps): JSX.Element {
  return (
    <nav
      aria-label={label}
      className={cx(
        // `w-sidenav` and `min-w-sidenav` together: the rail must not be squeezed by a wide table in
        // the main region, which is what happens with a width alone inside a flex row.
        "flex w-sidenav min-w-sidenav flex-col border-e border-borderSubtle bg-surfaceSunken",
        className,
      )}
    >
      {brand ? (
        // Matches the main region's header height exactly, so the rule under the logo and the rule
        // under the page title are one continuous line across the window rather than two near-misses.
        <div className="flex h-header shrink-0 items-center border-b border-borderSubtle px-4">
          {brand}
        </div>
      ) : null}

      {action ? <div className="shrink-0 p-4">{action}</div> : null}

      {/* A `ul`, so the number of destinations is announced, and so every `SideNavItem`'s `li` has a
          list parent. Items rendered straight into a `div` produced orphan `li` elements, which the
          browser gave default bullet markers — four dots down the rail.

          Scrolls on its own: the conversation history is unbounded, and a rail that grew the page
          would put the footer — and the sign-out control — below the fold. */}
      <ul className="m-0 flex min-h-0 flex-1 list-none flex-col gap-1 overflow-y-auto px-4 pb-4">
        {children}
      </ul>

      {footer ? <div className="shrink-0 p-4">{footer}</div> : null}
    </nav>
  );
}

// ---------------------------------------------------------------------------------------

export interface SideNavGroupProps {
  /** "Today", "This week". Already translated. */
  label: string;
  children: ReactNode;
}

/**
 * A labelled run of items.
 *
 * The label is a real heading rather than a styled span, so a screen-reader user can move between
 * groups instead of reading the whole history to find where "Earlier" starts.
 */
export function SideNavGroup({ label, children }: SideNavGroupProps): JSX.Element {
  // `li > ul` rather than `section > ul`: the parent is a list, and a `section` between a `ul` and
  // its `li` children is invalid — the browser reparents it and the grouping is lost to a screen
  // reader. A nested list is the one structure that expresses "these items belong under this label".
  return (
    <li className="mb-5">
      <h3 className="px-1 pb-2 text-label-sm font-medium tracking-wide text-onPrimaryMuted">
        {label}
      </h3>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">{children}</ul>
    </li>
  );
}

// ---------------------------------------------------------------------------------------

export interface SideNavItemProps {
  children: ReactNode;
  active?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  /**
   * Where this item goes. Given one, the item renders as a link rather than a button.
   *
   * That distinction is not cosmetic. A destination is a link: it belongs in the status bar, it
   * opens in a new tab on a middle-click, and a screen reader announces it as somewhere to go
   * rather than as a control that does something. A button that changes the page is a button
   * lying about what it is.
   *
   * Left as optional because the rail also holds items that are not destinations — a conversation
   * that has not been given a URL yet, an action.
   */
  href?: string;
  /** Extra line under the label — a timestamp, a count. */
  meta?: ReactNode;
  className?: string;
}

export function SideNavItem({
  children,
  active = false,
  icon,
  onClick,
  href,
  meta,
  className,
}: SideNavItemProps): JSX.Element {
  // The class list and the contents are computed once and rendered into whichever element is honest
  // about what the item is. Two full components would drift the moment either was restyled; a
  // dynamic element type would need a cast that defeats the point of having types.
  //
  // The active state is a blue tint with a matching border, not a fill. On a rail that is itself
  // tinted, a bare tint has no edge and reads as a smudge; the border is what makes it read as a
  // selected row. Idle items carry a transparent border of the same width so selecting one does not
  // shift the text by a pixel.
  const classes = cx(
    "flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-start text-body-sm",
    "no-underline transition-colors",
    active
      ? "border-primaryBorder bg-primarySubtle font-semibold text-primary"
      : "border-transparent font-medium text-textMuted hover:bg-surfaceRaised hover:text-primary",
    className,
  );

  const contents = (
    <>
      {icon}
      <span className="flex min-w-0 flex-col">
        {/* `truncate` needs `min-w-0` on the flex child above it, or a long conversation title
            stretches the rail instead of being clipped. */}
        <span className="truncate">{children}</span>
        {meta ? <span className="truncate text-label-sm text-onPrimaryMuted">{meta}</span> : null}
      </span>
    </>
  );

  // `page`, not `true`: this marks the current *destination*, and `aria-current="page"` is what a
  // screen reader announces as "current page".
  const current = active ? ("page" as const) : undefined;

  return (
    <li className="list-none">
      {href ? (
        <RouteLink to={href} aria-current={current} className={classes} onClick={onClick}>
          {contents}
        </RouteLink>
      ) : (
        <button type="button" onClick={onClick} aria-current={current} className={classes}>
          {contents}
        </button>
      )}
    </li>
  );
}
